import { useCallback, useMemo, useRef } from 'react';

import { type Environment } from 'react-relay';

import { AnyAri } from '@atlassian/ari/any-ari';
import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { useUnitLinkedResourceIds } from '@atlassian/unit-linked-resources';

import type { Product } from './global-object-preview-panels';

const anchorBuilder: Partial<Record<Product, (siteId: string) => string>> = {
	jira: (siteId) => JiraSiteAri.create({ siteId }).toString(),
	confluence: (siteId) => ConfluenceSiteAri.create({ siteId }).toString(),
};

const buildAnchorAri = (product: Product, cloudId: string): string => {
	const build = anchorBuilder[product];
	return build && cloudId ? build(cloudId) : '';
};

const extractLinkedCloudIds = (linkedResourceIds: string[]): Set<string> => {
	const cloudIds = new Set<string>();
	for (const ari of linkedResourceIds) {
		try {
			const parsed = AnyAri.parse(ari);
			if (parsed.resourceType === 'site' && parsed.resourceId) {
				cloudIds.add(parsed.resourceId);
			}
		} catch {
			// Ignore unparseable ARIs.
		}
	}
	return cloudIds;
};

interface UseUnitLinkedResourcesResult {
	error: boolean;
	/**
	 * Returns `true` if `targetCloudId` belongs to the same unit as the current
	 * cloudId. Fails open (returns `true`) while loading/errored, when the target
	 * matches the current cloudId, or when the target has no cloudId.
	 */
	isCloudIdInSameUnit: (targetCloudId: string | undefined) => boolean;
	/** All linked-resource ARIs returned for the current site's unit. */
	linkedResourceIds: string[];
	loading: boolean;
}

/**
 * Pre-fetches the unit's linked resources for the current cloudId via the
 * `admin_unitLinkedResource` GraphQL query
 *
 * This hook is only reached behind the `preview_panel_unit_check` gate: the
 * top-level `useGlobalObjectPreviewPanels` selects `useGlobalObjectPreviewPanelsNew`
 * (its only caller) via `functionWithCondition`, so no additional gating is needed
 * here.
 */
export const useUnitLinkedResources = (
	relayEnvironment: Environment,
	product: Product,
	cloudId: string,
): UseUnitLinkedResourcesResult => {
	const anchorAri = buildAnchorAri(product, cloudId);
	const { linkedResourceIds, loading, error } = useUnitLinkedResourceIds(
		relayEnvironment,
		anchorAri,
	);

	const linkedCloudIds = useMemo(
		() => extractLinkedCloudIds(linkedResourceIds),
		[linkedResourceIds],
	);

	// Read via a ref so isCloudIdInSameUnit keeps a stable identity across fetches.
	const stateRef = useRef({ linkedCloudIds, loading, error });
	stateRef.current = { linkedCloudIds, loading, error };

	const isCloudIdInSameUnit = useCallback(
		(targetCloudId: string | undefined): boolean => {
			const { linkedCloudIds, loading, error } = stateRef.current;

			// Skipped (e.g. home/teams), no target, same cloudId, loading, or errored → allow.
			if (!anchorAri || !targetCloudId || targetCloudId === cloudId || loading || error) {
				return true;
			}

			return linkedCloudIds.has(targetCloudId);
		},
		[anchorAri, cloudId],
	);

	return {
		linkedResourceIds,
		loading,
		error,
		isCloudIdInSameUnit,
	};
};
